/** P02 5.2.3.1 — Valoración del Expediente de Compra (VEC) */

export function normalizeSupplierEvalField(v) {
  if (v === null || v === undefined) return null;
  if (typeof v === "number") {
    if (Number.isNaN(v) || !Number.isFinite(v)) return null;
    if (v < 0 || v > 10) return null;
    return v;
  }
  if (v === "") return null;
  let s = String(v)
    .replace(/,/g, ".")
    .replace(/[‑\-–—]/g, "")
    .trim();
  if (s === "") return null;
  const n = Number(s);
  if (Number.isNaN(n) || !Number.isFinite(n)) return null;
  if (n < 0 || n > 10) return null;
  return n;
}

/** True when the user entered a score (including 0). False for null/empty/invalid. */
export function isSupplierEvalFieldFilled(v) {
  return normalizeSupplierEvalField(v) !== null;
}

/** Display: "—" until filled; 0 must show as "0". */
export function formatSupplierEvalDisplay(v) {
  const n = normalizeSupplierEvalField(v);
  if (n === null) return "—";
  return Number.isInteger(n) ? String(n) : String(Number(n.toFixed(2)));
}

/**
 * Ofertas con importe positivo para calcular VP.
 * @param {Array<{ empresaProveedora?: string, importe?: number }>} offers
 */
export function getValidOffersForVp(offers) {
  if (!offers || !offers.length) return [];
  return offers.filter((o) => {
    if (!o) return false;
    const hasProvider = !!(
      o.empresaProveedora && String(o.empresaProveedora).trim()
    );
    const importe = Number(o.importe);
    if (!hasProvider || !importe || importe <= 0) return false;
    if (Array.isArray(o.valid) && o.valid[0] !== true) return false;
    return true;
  });
}

function findAwardedOffer(validOffers, awardedOfferId) {
  if (awardedOfferId == null || awardedOfferId === "") return null;
  const idStr = String(awardedOfferId);
  return (
    validOffers.find((o) => String(o.id) === idStr) ||
    validOffers.find((o) => o.id == awardedOfferId)
  );
}

/**
 * VP automático según P02 5.2.3.1:
 * - Si solo se dispuso de una oferta: VP = 10
 * - Si se dispuso de más de una oferta: VP = (oferta más económica / oferta valorada) × 10
 */
export function calcularVPFromOffers(offers, awardedOfferId, awardedAmount) {
  const validOffers = getValidOffersForVp(offers);
  if (validOffers.length === 0) return null;
  if (validOffers.length === 1) return 10;

  const minImporte = Math.min(...validOffers.map((o) => Number(o.importe)));

  let valorada = Number(awardedAmount);
  const awarded = findAwardedOffer(validOffers, awardedOfferId);
  if (awarded) valorada = Number(awarded.importe);

  if (!valorada || valorada <= 0 || !minImporte) return null;

  const vp = (minImporte / valorada) * 10;
  return Math.min(10, Math.max(0, Number(vp.toFixed(2))));
}

/** VEC = VCa×50% + VCp×30% + VI×10% + VP×10% */
export function calcularVECFromFields(evals) {
  const vCa = normalizeSupplierEvalField(evals.VCa);
  const vCp = normalizeSupplierEvalField(evals.VCp);
  const vI = normalizeSupplierEvalField(evals.VI);
  const vP = normalizeSupplierEvalField(evals.VP);

  if (vCa === null || vCp === null || vI === null || vP === null) return null;

  const vec = vCa * 0.5 + vCp * 0.3 + vI * 0.1 + vP * 0.1;
  return Number(vec.toFixed(2));
}

const MANUAL_EVAL_FIELDS = ["VCa", "VCp", "VI"];

const MANUAL_EVAL_LABELS = {
  VCa: "Calidad",
  VCp: "Cumplimiento del plazo",
  VI: "Interlocución",
};

/**
 * @returns {{ complete: boolean, missingManual: string[], vp: number|null }}
 */
export function validateSupplierEvaluation(
  evals,
  offers,
  awardedOfferId,
  awardedAmount
) {
  const missingManual = MANUAL_EVAL_FIELDS.filter(
    (field) => !isSupplierEvalFieldFilled(evals[field])
  );
  const vp = calcularVPFromOffers(offers, awardedOfferId, awardedAmount);
  return {
    complete: missingManual.length === 0 && vp !== null,
    missingManual,
    vp,
  };
}

/**
 * Reason (message shown to the user) why the supplier evaluation blocks
 * closing the EC, or null when it does not block.
 *
 * Rules:
 * - isApply not decided (null/undefined): blocks — the user must choose Sí/No.
 * - isApply === false: blocks until a "Motivo" is given.
 * - isApply === true: blocks until all manual scores are filled and VP is computable.
 */
export function getSupplierEvalCloseBlockReason(
  evaluation,
  offers,
  awardedOfferId,
  awardedAmount
) {
  const evalObj = evaluation || {};

  if (evalObj.isApply !== true && evalObj.isApply !== false) {
    return 'Debe indicar si aplica la evaluación del proveedor (Sí/No) en la sección "Evaluación de empresa proveedora".';
  }

  if (evalObj.isApply === false) {
    const hasReason =
      evalObj.reason && String(evalObj.reason).trim().length > 0;
    if (!hasReason) {
      return "Debe indicar el Motivo por el que no aplica la evaluación del proveedor.";
    }
    return null;
  }

  const { complete, missingManual, vp } = validateSupplierEvaluation(
    evalObj,
    offers,
    awardedOfferId,
    awardedAmount
  );
  if (complete) return null;

  if (missingManual.length > 0) {
    const names = missingManual
      .map((f) => MANUAL_EVAL_LABELS[f] || f)
      .join(", ");
    return `Debe valorar al proveedor (0-10) en: ${names}. Use "—" solo como indicador de campo vacío; el 0 es una nota válida.`;
  }
  if (vp === null) {
    return "No se puede calcular el Precio (VP): revise que existan ofertas válidas y empresa proveedora adjudicada.";
  }
  return "Debe completar la valoración del proveedor antes de cerrar el Expediente.";
}

/**
 * Explains the auto-calculated VP shown in the "Precio" field (P02 5.2.3.1),
 * so a value of 10 is understandable (single offer or cheapest offer awarded).
 */
export function getVpExplanation(offers, awardedOfferId, awardedAmount) {
  const validOffers = getValidOffersForVp(offers);

  if (validOffers.length === 0) {
    return "Calculado automáticamente: aún no hay ofertas válidas con importe para calcular el Precio (VP).";
  }
  if (validOffers.length === 1) {
    return "Calculado automáticamente: VP = 10 porque solo se dispone de una oferta válida.";
  }

  const minImporte = Math.min(...validOffers.map((o) => Number(o.importe)));
  let valorada = Number(awardedAmount);
  const awarded = findAwardedOffer(validOffers, awardedOfferId);
  if (awarded) valorada = Number(awarded.importe);

  if (!valorada || valorada <= 0) {
    return "Calculado automáticamente según ofertas válidas y adjudicación.";
  }
  if (valorada <= minImporte) {
    return "Calculado automáticamente: VP = 10 porque la oferta adjudicada es la más económica.";
  }
  return "Calculado automáticamente: VP = (oferta más económica / oferta adjudicada) × 10.";
}

/**
 * Legacy records defaulted isApply to true without user input.
 * Treat those as "not decided": the user must confirm Sí/No before closing.
 */
export function normalizeSupplierCompEvaluation(evaluation) {
  if (!evaluation || evaluation.isApply !== true) return evaluation;
  const hasManualScore = MANUAL_EVAL_FIELDS.some((field) =>
    isSupplierEvalFieldFilled(evaluation[field])
  );
  const hasReason =
    evaluation.reason && String(evaluation.reason).trim().length > 0;
  if (!hasManualScore && !hasReason && evaluation.VEC == null) {
    evaluation.isApply = null;
  }
  return evaluation;
}

/** Sync VP + VEC on evaluation object (mutates evals). */
export function finalizeSupplierEvaluation(
  evals,
  offers,
  awardedOfferId,
  awardedAmount
) {
  const { complete, vp } = validateSupplierEvaluation(
    evals,
    offers,
    awardedOfferId,
    awardedAmount
  );
  evals.VP = vp;
  if (!complete) {
    evals.VEC = null;
    return null;
  }
  const vec = calcularVECFromFields(evals);
  evals.VEC = vec;
  return vec;
}

/** Parse user input from v-text-field; returns null if empty/invalid. Allows 0. */
export function parseSupplierEvalInput(raw) {
  if (raw === null || raw === undefined) return null;
  const text =
    raw && typeof raw === "object" && raw.target != null
      ? raw.target.value
      : raw;
  let normalized =
    text === null || text === undefined
      ? ""
      : String(text).replace(/,/g, ".").trim();
  normalized = normalized.replace(/[‑\-–—]/g, "").trim();
  if (/^[‑\-–—\s]*$/.test(normalized) || normalized === "") return null;
  const v = Number(normalized);
  if (Number.isNaN(v) || !Number.isFinite(v) || v < 0 || v > 10) return null;
  return v;
}

/** P02 5.2.3.2 — Valoración de la empresa proveedora (VEP) */

const VEP_NEUTRAL = 5;
const DAYS_PER_YEAR = 365.25;

/** α(a): vigencia de la valoración según antigüedad en años */
export function alphaVigencia(ageYears) {
  const a = Math.max(0, ageYears);
  if (a >= 3) return 0;
  return Math.pow(1 - a / 3, 2);
}

/** VEC_it = 5 + α(a) × (VEC_i − 5) */
export function calcularVECAdjustado(vec, ageYears) {
  const vecNum = Number(vec);
  if (Number.isNaN(vecNum) || !Number.isFinite(vecNum)) return VEP_NEUTRAL;
  const a = alphaVigencia(ageYears);
  return VEP_NEUTRAL + a * (vecNum - VEP_NEUTRAL);
}

/** Parse rating date (dd/mm/yyyy from toLocaleDateString). */
export function parseRatingDate(dateStr) {
  if (!dateStr) return null;
  const p = String(dateStr).split("/").map(Number);
  if (p.length !== 3 || p.some((n) => Number.isNaN(n))) return null;
  const d = new Date(p[2], p[1] - 1, p[0]);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Antigüedad en años: (t − t_i) / 365.25 */
export function calcularAntiguedadAnios(
  ratingDate,
  referenceDate = new Date()
) {
  const parsed = parseRatingDate(ratingDate);
  if (!parsed) return 0;
  const ms = referenceDate.getTime() - parsed.getTime();
  return ms / (DAYS_PER_YEAR * 24 * 60 * 60 * 1000);
}

/**
 * Ratings de EC cerrados (5-Expediente cerrado).
 * @param {Array} ratings
 * @param {Array} contracts
 */
export function filterClosedContractRatings(ratings, contracts) {
  if (!ratings || !ratings.length) return [];
  if (!contracts || !contracts.length) return [];
  return ratings.filter((r) => {
    if (!r?.contractID) return false;
    const contrato = contracts.find((c) => c.id === r.contractID);
    return contrato && contrato.contractState === "5-Expediente cerrado";
  });
}

/**
 * VEP(t) = (1/n) × Σ VEC_it — P02 5.2.3.2
 * @returns {number} 0–10, neutral 5 when no closed EC ratings
 */
export function calcularVEP(ratings, contracts, referenceDate = new Date()) {
  const ratingsFiltradas = filterClosedContractRatings(ratings, contracts);
  if (ratingsFiltradas.length === 0) return VEP_NEUTRAL;

  const adjusted = ratingsFiltradas.map((r) => {
    const ageYears = calcularAntiguedadAnios(r.date, referenceDate);
    return calcularVECAdjustado(r.rate, ageYears);
  });

  const sum = adjusted.reduce((s, v) => s + v, 0);
  return Number((sum / adjusted.length).toFixed(2));
}

/** VEP formatted for display (always "X.XX"). */
export function formatVEP(ratings, contracts, referenceDate = new Date()) {
  return calcularVEP(ratings, contracts, referenceDate).toFixed(2);
}

/** Apta si VEP ≥ 5 y documentación válida */
export function isSupplierAptaByVEP(
  ratings,
  contracts,
  hasValidDocs,
  referenceDate = new Date()
) {
  return (
    calcularVEP(ratings, contracts, referenceDate) >= VEP_NEUTRAL &&
    hasValidDocs
  );
}
